/**
 * VulnerabilityModeToggle - Toggle component for vulnerability status display mode.
 *
 * Allows switching between:
 * - Legacy mode: Uses "Open"/"Pending" labels, hides Triaged vulnerabilities
 * - New mode: Uses "Demonstrated"/"Detected" labels, shows all vulnerabilities
 *
 * Uses React 19's useOptimistic hook for instant UI feedback with automatic rollback.
 *
 * @example
 * ```tsx
 * // Full component with card wrapper
 * <VulnerabilityModeToggle customerEmail="customer@example.com" />
 *
 * // Row-only for embedding in other cards
 * <VulnerabilityModeToggleRow customerEmail="customer@example.com" />
 * ```
 */

import { useMemo, useOptimistic } from 'react';
import { toast } from 'sonner';

import { ToggleSwitch } from '@/components/ui';
import { useMy } from '@/hooks';
import { useUpdateSetting } from '@/hooks/useSetting';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';

const SETTING_NAME = 'vulnerability-status-system';

interface VulnerabilityModeToggleProps {
  /** Customer email for impersonation support */
  customerEmail: string;
}

/**
 * Hook to manage vulnerability mode toggle state and API calls.
 * Extracted for reuse between full component and row component.
 */
function useVulnerabilityModeToggle(customerEmail: string) {
  const { data: settings } = useMy(
    { resource: 'setting' },
    { email: customerEmail }
  );
  const { mutateAsync: updateSetting } = useUpdateSetting(customerEmail);

  // Server state (single source of truth from API)
  const serverMode = useMemo<VulnerabilityStatusMode>(() => {
    const setting = settings?.find(s => s.name === SETTING_NAME);
    return setting?.value === 'new' ? 'new' : 'legacy';
  }, [settings]);

  // Optimistic state with automatic rollback on error
  const [optimisticMode, setOptimisticMode] = useOptimistic(
    serverMode,
    (_current, newMode: VulnerabilityStatusMode) => newMode
  );

  const handleToggle = async (checked: boolean) => {
    const newMode: VulnerabilityStatusMode = checked ? 'new' : 'legacy';

    try {
      // 1. Optimistic update (immediate UI feedback)
      setOptimisticMode(newMode);

      // 2. API call
      await updateSetting({
        name: SETTING_NAME,
        value: newMode,
        silent: true,
      });

      // 3. Success feedback
      toast.success('Vulnerability status mode updated successfully');
    } catch (error) {
      // 4. Automatic rollback by useOptimistic
      toast.error('Failed to update setting. Please try again.');
      console.error('Failed to update vulnerability status mode:', error);
    }
  };

  return { optimisticMode, handleToggle };
}

/**
 * Row-only toggle component for embedding in other cards (e.g., PraetorianSettingsContent).
 *
 * Uses React 19's useOptimistic hook for:
 * - Instant UI feedback (toggle updates immediately on click)
 * - Automatic rollback on error (no manual state management)
 * - Type-safe state transitions
 */
export function VulnerabilityModeToggleRow({
  customerEmail,
}: VulnerabilityModeToggleProps) {
  const { optimisticMode, handleToggle } =
    useVulnerabilityModeToggle(customerEmail);

  return (
    <div className="border-chariot-stroke rounded-md border p-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-chariot-text-primary text-base font-bold">
            New Vulnerability Status System
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            Show all detected vulnerabilities to customers with updated status
            labels (Demonstrated/Detected instead of Open/Pending)
          </p>
        </div>
        <ToggleSwitch
          checked={optimisticMode === 'new'}
          onChange={handleToggle}
          id="vulnerability-status-system"
        />
      </div>
    </div>
  );
}

/**
 * Full toggle component with card wrapper for standalone usage.
 *
 * Uses React 19's useOptimistic hook for:
 * - Instant UI feedback (toggle updates immediately on click)
 * - Automatic rollback on error (no manual state management)
 * - Type-safe state transitions
 */
export function VulnerabilityModeToggle({
  customerEmail,
}: VulnerabilityModeToggleProps) {
  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-xl font-bold">Vulnerability Display Settings</h3>
        </div>
        <VulnerabilityModeToggleRow customerEmail={customerEmail} />
      </div>
    </div>
  );
}
